import { HYDRATABLE_SLICES } from 'data/error-log-constants';
import {
  ConsoleEntryType,
  ConsoleLevelType,
  HydratableSliceType,
  InteractionTargetType,
  InteractionType,
  ReduxActionBreadcrumbType,
  ReduxSnapshotType,
  TriggerActionType
} from 'types/error-log';

const CONSOLE_LEVELS: ConsoleLevelType[] = [
  'log',
  'info',
  'warn',
  'error',
  'debug'
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function parseNullableString(value: unknown): string | null {
  if (typeof value === 'string') {
    return value;
  }
  return null;
}

function parseTarget(value: unknown): InteractionTargetType | null {
  if (!isRecord(value) || typeof value.tag !== 'string') {
    return null;
  }
  if (typeof value.path !== 'string') {
    return null;
  }
  return {
    tag: value.tag,
    id: parseNullableString(value.id),
    text: parseNullableString(value.text),
    ariaLabel: parseNullableString(value.ariaLabel),
    name: parseNullableString(value.name),
    dataTestId: parseNullableString(value.dataTestId),
    path: value.path
  };
}

function parseInteraction(value: unknown): InteractionType | null {
  if (!isRecord(value)) {
    return null;
  }
  if (value.type !== 'click' && value.type !== 'keydown') {
    return null;
  }
  if (typeof value.id !== 'number' || typeof value.timestamp !== 'number') {
    return null;
  }
  const target = parseTarget(value.target);
  if (!target) {
    return null;
  }
  if (typeof value.key === 'string') {
    return {
      id: value.id,
      timestamp: value.timestamp,
      type: value.type,
      key: value.key,
      target
    };
  }
  return {
    id: value.id,
    timestamp: value.timestamp,
    type: value.type,
    target
  };
}

function parseBreadcrumb(value: unknown): ReduxActionBreadcrumbType | null {
  if (!isRecord(value)) {
    return null;
  }
  if (typeof value.id !== 'number') {
    return null;
  }
  if (typeof value.timestamp !== 'number') {
    return null;
  }
  if (typeof value.type !== 'string') {
    return null;
  }
  return {
    id: value.id,
    timestamp: value.timestamp,
    type: value.type
  };
}

function parseList<T>(
  value: unknown,
  parseItem: (item: unknown) => T | null
): T[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((item) => {
    const parsed = parseItem(item);
    return parsed ? [parsed] : [];
  });
}

export function parseTriggerAction(value: unknown): TriggerActionType | null {
  if (!isRecord(value)) {
    return null;
  }
  return {
    lastInteraction: parseInteraction(value.lastInteraction),
    recentInteractions: parseList(value.recentInteractions, parseInteraction),
    recentReduxActions: parseList(value.recentReduxActions, parseBreadcrumb)
  };
}

function parseConsoleLevel(value: unknown): ConsoleLevelType | null {
  if (typeof value !== 'string') {
    return null;
  }
  return CONSOLE_LEVELS.includes(value as ConsoleLevelType)
    ? (value as ConsoleLevelType)
    : null;
}

function parseConsoleEntry(value: unknown): ConsoleEntryType | null {
  if (!isRecord(value)) {
    return null;
  }
  const level = parseConsoleLevel(value.level);
  if (!level) {
    return null;
  }
  if (typeof value.id !== 'number' || typeof value.timestamp !== 'number') {
    return null;
  }
  if (typeof value.message !== 'string') {
    return null;
  }
  return {
    id: value.id,
    timestamp: value.timestamp,
    level,
    message: value.message
  };
}

export function parseConsoleSnapshot(
  value: unknown
): ConsoleEntryType[] | null {
  if (value == null) {
    return null;
  }
  if (!Array.isArray(value)) {
    return null;
  }
  return parseList(value, parseConsoleEntry);
}

function parseDroppedSlices(value: unknown): HydratableSliceType[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((item) => {
    if (
      typeof item === 'string' &&
      (HYDRATABLE_SLICES as readonly string[]).includes(item)
    ) {
      return [item as HydratableSliceType];
    }
    return [];
  });
}

export function parseReduxSnapshot(value: unknown): ReduxSnapshotType | null {
  if (!isRecord(value) || !isRecord(value.slices)) {
    return null;
  }
  const meta = isRecord(value.meta) ? value.meta : {};
  return {
    slices: value.slices as ReduxSnapshotType['slices'],
    meta: {
      schemaVersion:
        typeof meta.schemaVersion === 'number' ? meta.schemaVersion : 0,
      capturedAt: typeof meta.capturedAt === 'number' ? meta.capturedAt : 0,
      droppedSlices: parseDroppedSlices(meta.droppedSlices)
    }
  };
}
